/*import { FC, useState } from "react";
import { Modal, Button, Input, Loader } from "rsuite";
import { Unity } from "../../../../domain/unity";

type Props = {
  visible?: boolean;
  onSearch: (keyword: string) => void;
  loading?: boolean;
  result?: Unity[];
  onCancel: () => void;
};

export const SearchUnityModal: FC<Props> = ({
  visible,
  onSearch,
  loading,
  result,
  onCancel,
}) => {
  const [term, setTerm] = useState("");
  const handleSearch = () => {
    if (term.trim()) {
      onSearch(term.trim());
    }
  };

  return (
    <Modal open={visible} onClose={onCancel} size="md">
      <Modal.Header>
        <Modal.Title>Buscar Unidad</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Input
          placeholder="Buscar por nombre o ID"
          value={term}
          onChange={setTerm}
          disabled={loading}
          onPressEnter={handleSearch}
        />
        {loading && (
          <div className="text-center mt-3">
            <Loader center content="Buscando..." />
          </div>
        )}
        {!loading && result && result.length > 0 && (
          <ul className="mt-3">
            {result.map((u) => (
              <li key={u.id}>{u.label}</li>
            ))}
          </ul>
        )}
        {!loading && result && result.length === 0 && (
          <div className="mt-3 text-muted">No se encontraron resultados.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleSearch} appearance="primary" disabled={loading}>
          Buscar
        </Button>
        <Button onClick={onCancel} appearance="subtle">
          Cancelar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};*/

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Form, InputGroup, Modal, Schema } from "rsuite";
import { Search } from "@rsuite/icons";
import { useNavigate } from "react-router-dom";
import { Unity } from "../../../../domain/unity";
import { isEmpty } from "lodash";
import { isNotBlank } from "../../../../util/validation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { UnityAttendance } from "../../unity/attendance";

// Componente para listar resultados (puedes personalizarlo o moverlo a un archivo)
const UnitySearchListWindow: FC<{
  items: Unity[] | null | undefined;
  onClick: (unity: Unity) => void;
}> = ({ items, onClick }) => {
  if (!items || items.length === 0) {
    return (
      <div className="text-muted p-3">
        <FontAwesomeIcon icon={faSearch} />
        <span> No se encontraron resultados.</span>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      {items.map((u) => (
        <div
          key={u.id}
          className="p-2 border-b hover:bg-gray-100 cursor-pointer"
          onClick={() => onClick(u)}
        >
          <strong>{u.label}</strong>
          <div className="text-sm text-gray-500">{u.id}</div>
        </div>
      ))}
    </div>
  );
};

// Modal principal
type Props = {
  onSearch?: (keyword: string) => void;
  results: Unity[] | null;
  loading?: boolean;
  onClose?: () => void;
};

export const SearchUnityModal: FC<Props> = ({
  onSearch,
  onClose,
  results,
  loading,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  const onClick = useCallback(
    (unity: Unity) => {
      navigate(`/dashboard/unity/${unity.id}/main`);
      onClose?.(); // cerramos el modal al hacer clic
    },
    [navigate, onClose]
  );

  useEffect(() => {
    if (keyword.length >= 2 && onSearch && !isEmpty(keyword)) {
      clearTimeout(timeoutRef.current!);
      timeoutRef.current = setTimeout(() => {
        onSearch(keyword);
      }, 1000);
    }
  }, [keyword, onSearch]);

  return (
    <Modal open={true} onClose={onClose} size="md">
      <Modal.Header>
        <Modal.Title>Buscar Unidad</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-2 w-full h-fit">
          <Form
            onSubmit={(proceed, e) => {
              e.preventDefault();
              if (proceed && onSearch) onSearch(keyword);
            }}
            fluid
            model={Schema.Model({
              search: Schema.Types.StringType()
                .isRequired("Debe ingresar al menos 2 caracteres.")
                .minLength(2, "Ingrese mínimo 2 caracteres.")
                .addRule(
                  isNotBlank(1),
                  "El término no puede estar vacío o ser solo espacios."
                ),
            })}
          >
            <Form.Group>
              <InputGroup inside>
                <Form.Control
                  name="search"
                  onChange={(value: string) => setKeyword(value)}
                  value={keyword}
                  placeholder="Ingrese nombre o ID de unidad"
                  disabled={loading}
                />
                <InputGroup.Button type="submit" disabled={loading}>
                  <Search />
                </InputGroup.Button>
              </InputGroup>
            </Form.Group>
          </Form>
          <div className="w-full h-96 border rounded">
            <UnitySearchListWindow items={results} onClick={onClick} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
